import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { API_URL } from '@core/constants';
import { ApplicationStateService } from '@core/service/application-state.service';
import {
  IImmediateContactDetails,
  IOrderingProviderContactInfo,
  IPortalNotes,
  IPortalNotesResponse,
  IServicingProviderContactInfo
} from '@core/model';

@Injectable({
  providedIn: 'root',
})
export class PortalNotesService {
  constructor(
    private http: HttpClient,
    private applicationStateService: ApplicationStateService,
    @Inject(API_URL) private apiUrl: string
  ) {}

  /**
   * Get portal notes of the draft authorization
   * if getting null for providers means that provider note is not exist
   */
  getPortalNotes(authorizationByProductId: string): Observable<IPortalNotes> {
    return this.http.get<IPortalNotesResponse>(`${this.getBaseUrl(authorizationByProductId)}`)
      .pipe(map((res) => {
        const { immediateContactDetails, servicingProviderContactInfo, orderingProviderContactInfo } = res || {};
        return {
          contactInformation: immediateContactDetails ? {
            contactName: immediateContactDetails.name,
            contactFax: immediateContactDetails.fax,
            contactPhone: immediateContactDetails.phone,
            contactPhoneExtension: immediateContactDetails.phoneExtension,
          } : null,
          servicingProvider: servicingProviderContactInfo ? {
            npi: servicingProviderContactInfo.npi,
            providerName: servicingProviderContactInfo.providerName,
            phone: servicingProviderContactInfo.phone,
            fax: servicingProviderContactInfo.fax,
            street: servicingProviderContactInfo.address,
            city: servicingProviderContactInfo.city,
            state: servicingProviderContactInfo.state,
            zip: servicingProviderContactInfo.zipCode,
          } : null,
          orderingProvider: orderingProviderContactInfo ? {
            firstName: orderingProviderContactInfo.firstName,
            lastName: orderingProviderContactInfo.lastName,
            practiceNpinumber: orderingProviderContactInfo.practiceNpiNumber,
            physicalStreet: orderingProviderContactInfo.physicalStreet,
            physicalCity: orderingProviderContactInfo.physicalCity,
            physicalState: orderingProviderContactInfo.physicalState,
            physicalZip: orderingProviderContactInfo.physicalZip,
            phone: orderingProviderContactInfo.phone,
          } : null,
        };
      }));
  }

  /**
   * Create or update immediate contact details draft portal note
   */
  createUpdateImmediateContact(
    authorizationByProductId: string,
    contactData: IImmediateContactDetails
  ): Observable<IImmediateContactDetails> {
    return this.http.put<IImmediateContactDetails>(
      `${this.getBaseUrl(authorizationByProductId)}/immediate-contact`,
      contactData
    );
  }

  /**
   * Create or update servicing provider details draft portal note
   * null as body for delete portal note
   */
  createUpdateServicingProvider(
    authorizationByProductId: string,
    provider: IServicingProviderContactInfo = null
  ): Observable<IServicingProviderContactInfo> {
    return this.http.put<IServicingProviderContactInfo>(
      `${this.getBaseUrl(authorizationByProductId)}/servicing-provider`,
      provider
    );
  }

  /**
   * Create or update ordering provider details draft portal note
   * null as body for delete portal note
   */
  createUpdateOrderingProvider(
    authorizationByProductId: string,
    provider: IOrderingProviderContactInfo = null
  ): Observable<IOrderingProviderContactInfo> {
    return this.http.put<IOrderingProviderContactInfo>(
      `${this.getBaseUrl(authorizationByProductId)}/ordering-provider`,
      provider
    );
  }

  private getBaseUrl(authId: string): string {
    const tenantId = this.applicationStateService.tenantId;
    return `${this.apiUrl}/api/tenants/${tenantId}/authorizations/${authId}/draft/notes`;
  }
}
